import { useState } from 'react';
import { api } from '@/client/api';
import { useProcedure } from '@/client/useProcedure';
import { setModel } from '@/store/store';
import { ChoiceRow } from './choice';
import { Section, SettingCard, SettingRow, SettingToggle, ToneChip } from './kit';

/** Settings → Trading Defaults.
 *
 *  What a fresh order ticket starts with. These are *defaults*, not limits — the thing that actually refuses a
 *  stopless order is the risk rule, and weakening that waits out a refractory delay (Lockout Settings). Two settings
 *  here look like controls and are not, so both say so:
 *
 *   · **Require a stop** pre-ticks the box. It does not enforce anything; Risk Rules does.
 *   · **Confirm before sending** can be turned off, and turning it off is audited with its before and after. It is
 *     the one setting on this screen that changes what happens between deciding and dispatching.
 *
 *  The default stop is a percentage rather than a number of points, because eight points is a sane gold stop and an
 *  absurd EURUSD one — the same reasoning the desk itself uses (DEC-060).
 */
const SYMBOLS = ['XAUUSD', 'EURUSD', 'GBPJPY', 'US30', 'NAS100', 'USDJPY', 'BTCUSD'];

export function TradingSection() {
  const { state, reload } = useProcedure(() => api.me.settings.get(), []);
  const [saving, setSaving] = useState(false);

  const save = async (patch: Parameters<typeof api.me.settings.update>[0]): Promise<void> => {
    setSaving(true);
    try {
      await api.me.settings.update(patch);
      reload();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Section state={state} reload={reload}>
      {(data) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <SettingCard
            title="The ticket"
            sub="What a fresh order ticket opens with. Changing these never touches an order already on the desk."
          >
            <SettingRow
              label="Default instrument"
              hint="What the desk loads when you open it. Picking a different one on the desk does not change this."
            >
              <select
                value={data.trading.defaultSymbol}
                disabled={saving}
                aria-label="Default instrument"
                onChange={(e) => {
                  const defaultSymbol = e.target.value;
                  // Applied to the live desk as well as stored: a default the trader has just chosen and cannot see
                  // take effect looks like it did not save.
                  setModel({ deskSym: defaultSymbol, ordSl: '', ordTp: '' });
                  void save({ trading: { defaultSymbol } });
                }}
                style={selectStyle(saving)}
              >
                {SYMBOLS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </SettingRow>

            <SettingRow label="Default size" hint="Lots. Your session cap still applies on top of this.">
              <Stepper
                value={data.trading.defaultLots}
                step={0.01}
                min={0.01}
                max={1}
                decimals={2}
                suffix=" lots"
                disabled={saving}
                label="Default size in lots"
                onChange={(defaultLots) => void save({ trading: { defaultLots } })}
              />
            </SettingRow>

            <SettingRow
              label="Default stop distance"
              hint="A percentage of price, so one setting travels across instruments — eight points is a sane gold stop and an absurd EURUSD one."
            >
              <Stepper
                value={data.trading.defaultStopPct * 100}
                step={0.05}
                min={0.05}
                max={5}
                decimals={2}
                suffix="%"
                disabled={saving}
                label="Default stop distance as a percentage of price"
                onChange={(pct) => void save({ trading: { defaultStopPct: pct / 100 } })}
              />
            </SettingRow>

            <ChoiceRow
              label="Default target"
              hint="The take profit distance, as a multiple of the stop."
              value={String(data.trading.defaultRewardRatio)}
              options={[
                ['1', '1:1'],
                ['2', '2:1'],
                ['3', '3:1'],
              ]}
              disabled={saving}
              onPick={(v) => void save({ trading: { defaultRewardRatio: Number(v) } })}
            />
          </SettingCard>

          <SettingCard
            title="Before an order leaves"
            sub="The only two settings on this screen that change what happens between deciding and dispatching."
          >
            <SettingRow
              label="Confirm before sending"
              hint={
                data.trading.confirmBeforeOrder
                  ? 'Every order shows a confirmation naming the instrument, side and size first.'
                  : 'Orders dispatch on the first click. Turning this off is recorded in your audit trail.'
              }
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '9px' }}>
                {data.trading.confirmBeforeOrder ? null : <ToneChip tone="warn">One click</ToneChip>}
                <SettingToggle
                  on={data.trading.confirmBeforeOrder}
                  label="Confirm before sending an order"
                  onClick={() =>
                    void save({ trading: { confirmBeforeOrder: !data.trading.confirmBeforeOrder } })
                  }
                />
              </span>
            </SettingRow>

            <SettingRow
              label="Require a stop"
              hint="Pre-ticks the stop on a new ticket. This is a default, not a limit — the rule that actually refuses a stopless order lives in Risk Rules, where loosening it waits."
            >
              <SettingToggle
                on={data.trading.requireStop}
                label="Require a stop loss on every entry"
                onClick={() => void save({ trading: { requireStop: !data.trading.requireStop } })}
              />
            </SettingRow>
          </SettingCard>
        </div>
      )}
    </Section>
  );
}

function selectStyle(disabled: boolean): React.CSSProperties {
  return {
    fontSize: '11.5px',
    fontFamily: 'inherit',
    color: 'var(--text-mid)',
    background: 'transparent',
    border: '1px solid var(--l8)',
    borderRadius: '99px',
    padding: '5px 11px',
    cursor: disabled ? 'default' : 'pointer',
  };
}

/** A number nudged rather than typed.
 *
 *  A free text field for a lot size invites `0.O1` and a stop of `1000`. Bounded steps make the wrong value hard to
 *  reach and the right one quick, which is the correct trade for a field whose units a trader is not thinking about
 *  while they set it. */
function Stepper({
  value,
  step,
  min,
  max,
  decimals,
  suffix,
  onChange,
  disabled,
  label,
}: {
  value: number;
  step: number;
  min: number;
  max: number;
  decimals: number;
  suffix: string;
  onChange: (value: number) => void;
  disabled: boolean;
  label: string;
}) {
  const clamp = (v: number): number => Math.min(max, Math.max(min, Number(v.toFixed(decimals))));
  const button = (delta: number, glyph: string, what: string) => (
    <button
      type="button"
      disabled={disabled || (delta < 0 ? value <= min : value >= max)}
      onClick={() => onChange(clamp(value + delta))}
      title={`${what} ${label}`}
      aria-label={`${what} ${label}`}
      style={{
        width: '22px',
        height: '22px',
        borderRadius: '9px',
        border: '1px solid var(--l8)',
        background: 'transparent',
        color: 'var(--muted)',
        cursor: disabled ? 'default' : 'pointer',
        fontSize: '12px',
        lineHeight: 1,
      }}
    >
      {glyph}
    </button>
  );

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
      {button(-step, '−', 'Decrease')}
      <span
        style={{
          fontSize: '11.5px',
          color: 'var(--text)',
          fontVariantNumeric: 'tabular-nums',
          minWidth: '58px',
          textAlign: 'center',
        }}
      >
        {value.toFixed(decimals)}
        {suffix}
      </span>
      {button(step, '+', 'Increase')}
    </span>
  );
}
